import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { motion } from 'framer-motion';
import { getLocalPlatformFee } from '../utils/pricingCopy';
import './pricing.css';

export default function PricingPage() {
  const [mode, setMode] = useState('member');
  const isMember = mode === 'member';
  const { feeLabel: localFeeLabel } = useMemo(() => getLocalPlatformFee('GBP'), []);

  const pricingItemsMember = [
    `About ${localFeeLabel} per member per year, collected with membership payment`,
    'No direct cost to your club',
    'Stripe handles payments securely',
    'You retain full fee control',
    'Ideal for clubs without upfront budgets'
  ];
  const pricingItemsClub = [
    `About ${localFeeLabel} per qualifying member per year in local currency`,
    'Annual payment based on members present for at least 24 hours',
    'Members pay nothing extra',
    'Perfect for grant-funded or subscription clubs',
    'Membership can be gated or collected later by the club'
  ];

  const features = [
    {
      title: '💡 Everything Included',
      items: [
        'Member onboarding & join forms',
        'Join requests & approvals',
        'Events calendar + reminders',
        'Messaging and tracked emails',
        'Custom profile fields',
        'Branding + public links',
        'Bolt-ons & add-on subscriptions',
        'Team availability settings',
        'Public join pages'
      ]
    },
    {
      title: '🛠️ Flexible Pricing Rules',
      items: [
        'Set pricing per member type',
        'Charge by age, role or activity',
        'Auto-apply tag-based fees',
        'Member- or club-paid modes',
        'Pro-rata fees for late joiners'
      ]
    },
    {
      title: '🤝 Payment Flexibility',
      items: [
        'Secure Stripe payments',
        'Debit & credit cards, Apple & Google Pay',
        'Parents can pay for children',
        'Transparent fee breakdowns',
        'Installment options (coming soon)',
        'Automated receipts & invoices'
      ]
    }
  ];

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="marketing-root pricing-page"
    >
      <div className="container py-12 space-y-12">
        {/* Header */}
        <section className="marketing-hero marketing-hero--compact">
          <div className="marketing-orb orb-one" />
          <div className="marketing-orb orb-two" />
          <div className="marketing-hero__layout">
            <div>
              <p className="marketing-kicker">Pricing</p>
              <h1 className="marketing-heading text-5xl font-bold mb-3">Simple, transparent pricing.</h1>
              <p className="marketing-lede">
                £1 per member per year. Unchanged since May 2025.
              </p>
              <p className="pricing-hero__promise">Guaranteed through 2030. No plans to raise it.</p>
              {localFeeLabel !== '£1' && (
                <p className="pricing-hero__local">About {localFeeLabel} per member per year in your local currency.</p>
              )}
            </div>
            <div className="marketing-hero__panel">
              <h3 className="marketing-heading text-2xl font-semibold">Always in your control</h3>
              <p className="text-gray-600 mt-2">
                Use hosted Stripe membership payments, or collect membership fees yourself and pay Member Nest annually.
              </p>
              <div className="pricing-card__meta">
                <span>✅ No setup fees</span>
                <span>✅ Cancel anytime</span>
                <span>✅ Accrued club-managed fees settled on closure</span>
                <span>✅ Flexible billing</span>
              </div>
            </div>
          </div>
        </section>

        <section className="pricing-promise" aria-labelledby="pricing-promise-heading">
          <div className="pricing-promise__intro">
            <span className="marketing-pill">A price lock for peace of mind</span>
            <h2 id="pricing-promise-heading" className="marketing-heading">£1 is our business model.</h2>
            <p>
              We’ve charged £1 per member per year since Member Nest began in May 2025.
              Keeping club software affordable is one of our core values, and that price is how we’ve built our business.
              This public price lock is our commitment to those values.
            </p>
          </div>
          <div className="pricing-promise__details">
            <p>
              <strong>We guarantee our £1 per member per year platform fee will stay the same
              through 31 December 2030.</strong> This applies whether your members pay or your club pays.
            </p>
            <p>
              The lock is here to give you peace of mind when planning ahead.
              2030 is the minimum horizon of our guarantee, not a date for a price rise.
              We have no plans to raise the price, including after 2030.
            </p>
            <p className="pricing-promise__note">
              The guarantee covers Member Nest’s £1 platform fee. Local currency equivalents
              may vary with conversion rates. Stripe payment processing fees are separate.
            </p>
          </div>
        </section>

        {/* Pill Toggle */}
        <section className="marketing-section">
          <div className="marketing-section__header">
            <h2 className="marketing-heading text-3xl font-semibold mb-2">Choose who pays</h2>
            <p className="marketing-lede">Switch between member-paid and club-paid pricing.</p>
          </div>
          <div className="flex justify-center">
            <div className="pill-toggle">
              <motion.div
                className={`pill-toggle__slider ${!isMember ? 'is-club' : ''}`}
                animate={{ left: isMember ? '2px' : 'calc(50% + 2px)' }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              />
              <div
                className={`pill-toggle__option ${isMember ? 'pill-toggle__option--active' : ''}`}
                onClick={() => setMode('member')}
              >
                Members Pay
              </div>
              <div
                className={`pill-toggle__option ${!isMember ? 'pill-toggle__option--active' : ''}`}
                onClick={() => setMode('club')}
              >
                Club Pays
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Card */}
        <section>
          <div className="pricing-card">
            <h3 className="text-2xl font-semibold mb-4">
              {isMember
                ? `💳 Add ${localFeeLabel} per member per year`
                : `🏦 Cover ${localFeeLabel} per active member per year`}
            </h3>
            <ul className="bullet-list">
              {(isMember ? pricingItemsMember : pricingItemsClub).map((text, i) => (
                <li key={i}>{text}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* Features */}
        <section className="marketing-section">
          <div className="marketing-section__header">
            <h2 className="marketing-heading text-3xl font-semibold mb-2">Everything included</h2>
            <p className="marketing-lede">All core tools are part of the plan from day one.</p>
          </div>
          <div className="marketing-grid">
            {features.map((sec) => (
              <Card key={sec.title} className="marketing-card">
                <h4 className="text-xl font-semibold mb-3">{sec.title}</h4>
                <ul className="bullet-list">
                  {sec.items.map((text) => (
                    <li key={text}>{text}</li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="marketing-band text-center">
          <div>
            <h2 className="marketing-heading text-3xl font-semibold mb-2">Ready to get started?</h2>
            <p className="marketing-lede">Create a club in minutes or book a walkthrough.</p>
          </div>
          <div className="pricing-page__actions flex justify-center gap-4">
            <Link to="/create-team" className="button button-primary px-6 py-2">
              Start your club
            </Link>
            <Link to="/demo" className="button button-secondary px-6 py-2">
              Book a demo
            </Link>
          </div>
        </section>
      </div>
    </motion.main>
  );
}
